import type {
	ValidationTypeOTP,
	DocumentosCopyBiometryAnalysisStatus,
	KYCBiometryAnalysisStatus,
	ReasonsCode,
} from "../../enum";
import type { AfterSaleProductTypeEnum, EAuthType } from "../../enum/authentication";
import type { HasPassword } from "../../enum/password";
import type { TemplateSMSEmail } from "../../enum/templateSMSEmail";

export interface Link {
	rel: string;
	href: string;
}

export interface APIResponse<APIResponseData> {
	data: APIResponseData;
	message: string;
	status: string;
	links?: Link[];
}

export interface PreSimulation {
	availableAmount: number;
}

export interface TotalSimulationInstallments {
	dueDate: string;
	duePrincipal: number;
	installmentNumber: number;
	taxAmount: number;
	preFixedAmount: number;
	principalAmortizationAmount: number;
	totalAmount: number;
}

export interface TotalSimulationResponse {
	disbursedIssueAmount: number;
	totalAmount: number;
	totalPreFixedAmount: number;
	iofAmount: number;
	annualCet: number;
	numberOfInstallments: number;
	yearsInAdvance: number;
	annualRate: number;
	dailyRate: number;
	interestBase: string;
	monthlyRate: number;
	installments: TotalSimulationInstallments[];
}

export interface AccessToken {
	accessToken: string;
}

export interface APIRequestBodyError {
	status: string;
	message: string;
	errors: FieldErrors[];
}
export interface FieldErrors {
	field: string;
	message: string;
}

export interface APIError {
	statusCode: number;
	message: string;
}

export interface APIServerError {
	errors: APIServerErrorList[];
	meta: APIServerErrorMeta;
}

export interface APIServerErrorList {
	code: string;
	title: string;
	detail: string;
}

export interface APIServerErrorMeta {
	totalRecords: number;
	totalPages: number;
	requestDateTime: string;
}

export interface BalanceInquiryRequest {
	availableBalanceKey: string;
	customerId: string;
	status: BalanceInquiryRequestStatusEnum;
	statusEvents: BalanceInquiryRequestStatusList[];
	error?: BalanceInquiryRequestError;
}

export interface BalanceInquiryRequestStatusList {
	eventDatetime: string;
	status: string;
}

export interface PartialAnticipationResponse {
	annualCet: number;
	annualRate: number;
	dailyRate: number;
	disbursedIssueAmount: number;
	installments: Installments[];
	interestBase: string;
	iofAmount: number;
	monthlyRate: number;
	numberOfInstallments: number;
	totalAmount: number;
	totalPreFixedAmount: number;
	yearsInAdvance: number;
	tacAmount: number;
}

export interface Installments {
	dueDate: string;
	duePrincipal: number;
	installment_number: number;
	taxAmount: number;
	preFixedAmount: number;
	principalAmortizationAmount: number;
	totalAmount: number;
}

export interface PartialAnticipationRequest {
	token: string;
	amount: string;
}
export interface BalanceInquiryRequestError {
	errorDescription: string;
	errorEnumerator: BalanceInquiryRequestErrorEnum;
}

export enum BalanceInquiryRequestErrorEnum {
	ONGOING_OPERATION = "ONGOING_OPERATION",
	UNAUTHORIZED_INSTITUTION = "UNAUTHORIZED_INSTITUTION",
	INEXISTENT_ANNIVERSARY_MEMBERSHIP = "INEXISTENT_ANNIVERSARY_MEMBERSHIP",
	ON_LOCKED_DATE_RANGE = "ON_LOCKED_DATE_RANGE",
	ANNIVERSARY_MEMBERSHIP_EGRESS = "ANNIVERSARY_MEMBERSHIP_EGRESS",
	PROCESSING_PENDING_CHANGES = "PROCESSING_PENDING_CHANGES",
	CAIXA_ERROR = "CAIXA_ERROR",
	LACK_PERIOD = "LACK_PERIOD",
	ONLY_ONE_OPERATION_ALLOWED_ANNUALLY = "ONLY_ONE_OPERATION_ALLOWED_ANNUALLY"
}

export enum BalanceInquiryRequestStatusEnum {
	pending = "pending",
	success = "success",
	failed = "failed",
}

interface LinksSendOTP {
	rel: string;
	href: string;
}

export interface SendOTPValidation {
	status: string;
	message: string;
	links: LinksSendOTP[];
}

export interface ValidateCodeOTPReq {
	id: string;
	token: string;
	authenticity: Authenticity;
}

export interface ValidateCodeOTPRes {
	validationId: string;
}

export interface Authenticity {
	timestamp: string;
	ipAddress: string;
	fingerprint: string;
	sessionId: string;
}

export interface SendOTPValidationReq {
	validationType: ValidationTypeOTP;
	contact?: string | null;
	authenticity?: Authenticity;
	templateCode?: TemplateSMSEmail;
}

export interface SendOTPValidationRes {
	id: string;
}

export interface NewPasswordReq {
	type: string;
	oldPassword?: string;
	newPassword: string;
	authenticity: Authenticity;
}

export interface CreatePasswordReq {
	password: string;
	type: string;
}

export interface Personal {
	customer: PersonalCustomer;
}

export interface PersonalCustomerFiliations {
	id?: string;
	type: string;
	fullName: string;
}

export interface PersonalCustomerContactsPhones {
	id?: string;
	provider?: string;
	countryCode: string;
	areaCode: string;
	number: string;
	isMain?: boolean;
	status?: string;
}

export interface PersonalCustomerContactsEmails {
	id?: string;
	isMain: boolean;
	email: string;
}
export interface PersonalCustomerContacts {
	phones?: PersonalCustomerContactsPhones[];
	emails: PersonalCustomerContactsEmails[];
}

export interface PersonalCustomer {
	id?: string;
	alreadyRegistration?: boolean;
	hasPassword: HasPassword;
	fullName: string;
	socialName: string;
	cpfNumber: string;
	birthDate: string;
	sex?: string;
	isPep: boolean;
	has4p: boolean;
	maritalStatusCode?: string;
	filiations?: PersonalCustomerFiliations[];
	contacts: PersonalCustomerContacts;
	address: PersonalPostAddressInfo[];
}

export interface SimpleAddressResponse {
	postalCode: string;
	address: string;
	additionalInfo: string;
	districtName: string;
	townName: string;
	countrySubDivision: string;
}

export interface PersonalInfo {
	fullName: string;
	socialName?: string;
	cpfNumber: string;
	birthDate: string | null;
}

export interface PersonalContactPhoneInfo {
	provider: string;
	countryCode: string;
	areaCode: string;
	number: string;
	isMain: true;
}

export interface PersonalContactEmailInfo {
	email: string;
	isMain: true;
}

export interface PersonalPostAddressInfo {
	id?: string;
	isMain: boolean;
	address: string;
	number: string;
	additionalInfo: string;
	districtName: string;
	townName: string;
	countrySubDivision: string;
	postalCode: string;
	country: string;
}

export interface PersonalAffiliationInfo {
	type: "MAE";
	fullName: string;
	socialName: string;
}

export interface SignatureData {
	disbursementBankAccount: {
		pixKey: string;
		pixTransferType: string;
	};
	simulationDetails: {
		type: string;
		amount: number;
		yearsInAdvance: number | null;
	};
	signatures: Signature[];
	seller: {
		sellerId: string;
	};
}

export interface Signature {
	signedObject: {
		rawText: string;
	};
	authenticity: {
		timestamp: string;
		ipAddress: string;
		sessionId: string;
		fingerprint: {
			lat?: number | null;
			long?: number | null;
			name?: string;
			model?: string;
			calendar?: string;
			regionCode?: string;
			systemName?: string;
			orientation?: string;
			currencyCode?: string;
			languageCode?: string;
			systemVersion?: string;
			userinterface?: string;
			localizedModel?: string;
			decimalSeparator?: string;
			usesMetricSystem?: string;
			preferredLanguages?: string;
			supportsMultiTasking?: string;
		};
	};
}

export interface PasswordValidate {
	password: string;
	authenticity: Authenticity;
}

export interface PasswordValidateResponse {
	data: {
		validationId: string;
	};
}

export interface VerifyEligibleResponse {
	data: {
		status: string;
		rules: Array<{
			message: string;
			name: string;
			status: boolean;
		}>;
	};
}

interface Code {
	code: ReasonsCode;
}

export interface FraudAnalysis {
	kyc: {
		id: string;
		status: KYCBiometryAnalysisStatus;
	};
	documentoscopy: {
		id: number;
		productId: string;
		status: DocumentosCopyBiometryAnalysisStatus;
		documentStatus: DocumentosCopyBiometryAnalysisStatus;
		selfieStatus: DocumentosCopyBiometryAnalysisStatus;
		reasonsCode?: Code[];
	};
}

export interface AlreadyRegistration {
	id: string;
	alreadyRegistration: boolean;
	productId: string;
	createdAt: string;
	updatedAt: string;
}

export interface ConsentingTerms {
	termIds: string[];
	authenticity: Authenticity;
}

/**
 * Types end Enums importados do EP na troca do login unico por login EP
 * begin
 */
export enum OtpValidationType {
	LOGIN_CROSS = "LOGIN_CROSS",
	LOGIN_VM = "LOGIN_VM"
}

export enum CustomerType {
	PRE = "pre-pago",
	POS = "pos-pago",
	HIBRIDO = "hibrido"
}

export enum CustomerStatus {
	OK = "OK",
	UNWELL = "UNWELL"
}

export enum SenderCode {
	VIVO = "vivo",
	CAPTALYS = "captalys"
}

export interface UserEligibility {
	customer: TCliente;
	phones: Array<{ number: string }>;
}

export interface TCliente {
	cpf: string;
	birthDate: string;
	customerName: string;
	email: string;
	segmentation: string;
	risks: string;
}

export interface OptIn {
	optIn?: boolean;
}

export interface IAuthParams {
	authType: EAuthType;
	validationType: OtpValidationType;
	userId: string;
	delivery: {
		method: string;
		contact: string;
	};
	nrdocumento: string;
	phone: string;
}

export interface IAuthValidationParams {
	publicIp: string;
	authType: EAuthType;
	validationType: OtpValidationType;
	token: string;
	userId?: string;
	nrdocumento: string;
	phone: string;
}

export interface IUserStatus {
	document: string;
	type: CustomerType;
	status: CustomerStatus;
	sender: SenderCode;
	smsCreated: Date;
}

export interface IUserActiveContracts {
	ep: boolean;
	fd: boolean;
}

export type ValidationErrorType = "blockedToken" | "exceededAttempts" | "expiredToken" | "invalidToken";

export interface TokenValidationError {
	attemptsLeft: number;
	erroType: ValidationErrorType;
}

export interface SimulationOffers {
	offers: SimulationOffer[];
}

export interface SimulationOffer {
	offerId: string;
	installments: number;
	value: number;
	monthlyCet: number;
	annualCet: number;
	amountDue: number;
	totalTaxAmount: number;
	iof: number;
	firstPaymentDate: string;
	lastPaymentDate: Date;
	monthlyTax: number;
}

export interface Offer {
	offerId: string;
	status: string;
	chosenVolume: number;
	expiresAt?: Date;
	amountDue: number;
	installmentValue: number;
	installments: number;
	monthlyTax: number;
	annualTax: number;
	registration: object;
	selectedDueDate: number;
	monthlyCet: number;
	annualCet: number;
	totalTaxAmount: number;
	iof: number;
	firstPaymentDate: Date;
	lastPaymentDate: Date;
	productId: AfterSaleProductTypeEnum;
	simulations: SimulationProposal[];
}

export interface SimulationProposal {
	number: number;
	date: Date;
	chosenVolume: number;
}

export interface Limit {
	limitId: string;
	expiresAt: Date;
	createdAt: Date;
	minCreditAvailable: number;
	maxCreditAvailable: number;
	monthlyTax: number;
	minParcelAmount: number;
	maxParcelAmount: number;
	policyVersion: string;
	status: string;
	monthlyInterestTax?: number;
	minInterestRate: number;
	maxInterestRate: number;
}

/**
 * Types importados do EP na troca do login unico por login EP
 * end
 */
